import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { parseApiError } from "../lib/apiError";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Textarea } from "../components/ui/textarea";
import {
  Loader2, Video, Lock, ChevronLeft, ChevronRight,
  ShieldCheck, FileSignature, Heart, User2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";
import { formatSlot, mergeSlots } from "../lib/booking";
import { PhoneField, CountrySelect } from "./ContactInputs";
import { DEFAULT_DIAL, DEFAULT_COUNTRY } from "../lib/countries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const HEALTH_FLAGS = [
  { key: "anxiety", label: "Severe anxiety or panic disorder" },
  { key: "depression", label: "Depression" },
  { key: "ptsd", label: "PTSD or trauma-related conditions" },
  { key: "bipolar", label: "Bipolar disorder, psychosis, or schizophrenia" },
  { key: "epilepsy", label: "Epilepsy or seizures" },
];

const ACK_ITEMS = [
  { key: "age", label: "I am 18 years of age or older." },
  { key: "voluntary", label: "I am participating voluntarily." },
  { key: "hypnosis", label: "I understand the nature of the session and consent to hypnosis." },
  { key: "responsibility", label: "I take responsibility for my physical, emotional, and mental wellbeing." },
  { key: "disclosure", label: "I have disclosed all relevant health information to the best of my knowledge." },
  { key: "complementary", label: "I understand these services are complementary and do not substitute for medical, psychological, or psychiatric care." },
];

const STEPS = [
  { id: 1, title: "Session", icon: Video },
  { id: 2, title: "About you", icon: User2 },
  { id: 3, title: "Health", icon: Heart },
  { id: 4, title: "Intentions", icon: ShieldCheck },
  { id: 5, title: "Consent", icon: FileSignature },
];

const initialIntake = {
  // Step 2 - personal
  first_name: "", last_name: "",
  dob: "", address: "", country: DEFAULT_COUNTRY, email: "",
  phone_dial: DEFAULT_DIAL, phone: "",
  emergency_name: "", emergency_phone: "",
  // Step 3 - health
  under_care: "", taking_medication: "",
  health_flags: { anxiety: "", depression: "", ptsd: "", bipolar: "", epilepsy: "" },
  health_details: "",
  // Step 4 - background + intentions
  current_challenges: "",
  fears_phobias: "",
  significant_events: "",
  spiritual_interests: "",
  intentions: ["", "", "", "", ""],
  // Step 5 - consent
  acknowledgements: { age: false, voluntary: false, hypnosis: false, responsibility: false, disclosure: false, complementary: false },
  recording_consent: "",
  esign_consent: false,
  signature_name: "",
};

export default function BookingModal({ open, service, onClose }) {
  // Booking core
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1);
    return d;
  });
  const [time, setTime] = useState("");
  const [format, setFormat] = useState("online");
  const [intake, setIntake] = useState(initialIntake);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [attempted, setAttempted] = useState(false); // true after user clicks Next/Pay once on current step
  const [availability, setAvailability] = useState({ blocked_weekdays: [], blocked_dates: [], blocked_times: {}, custom_times: [], custom_date_times: {} });
  const selectedIso = date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : "";
  const times = mergeSlots([
    ...(availability.custom_times || []),
    ...((selectedIso && availability.custom_date_times?.[selectedIso]) || []),
  ]);

  useEffect(() => {
    if (time && !times.includes(time)) setTime("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIso]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/availability`)
      .then((r) => setAvailability(r.data || { blocked_weekdays: [], blocked_dates: [], blocked_times: {} }))
      .catch(() => {});
  }, [open]);

  // Convert API weekday (0=Mon..6=Sun) to JS Date.getDay() (0=Sun..6=Sat)
  const isDateBlocked = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    const jsDow = d.getDay();              // 0=Sun..6=Sat
    const dowMonFirst = (jsDow + 6) % 7;   // 0=Mon..6=Sun
    if (availability.blocked_weekdays.includes(dowMonFirst)) return true;
    const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    if (availability.blocked_dates.includes(iso)) return true;
    return false;
  };

  const setField = (k, v) => setIntake((s) => ({ ...s, [k]: v }));
  const setNested = (parent, k, v) =>
    setIntake((s) => ({ ...s, [parent]: { ...s[parent], [k]: v } }));

  const fullReset = () => {
    const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1);
    setStep(1); setDate(d); setTime(""); setFormat("online");
    setIntake(initialIntake); setErr(""); setAttempted(false);
  };
  const handleClose = () => { fullReset(); onClose(); };

  // ----- Validation -----
  const anyHealthFlagYes = Object.values(intake.health_flags).some((v) => v === "yes");
  const anyYesInStep3 = intake.under_care === "yes" || intake.taking_medication === "yes" || anyHealthFlagYes;

  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!date) e.date = "Please choose a date.";
      if (!time) e.time = "Please choose a time.";
    }
    if (s === 2) {
      if (!intake.first_name.trim()) e.first_name = "First name is required.";
      if (!intake.last_name.trim()) e.last_name = "Last name is required.";
      if (!intake.dob) e.dob = "Date of birth is required.";
      if (!intake.email.trim()) e.email = "Email is required.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(intake.email)) e.email = "Please enter a valid email.";
      if (!intake.phone.trim()) e.phone = "Phone number is required.";
      if (!intake.address.trim()) e.address = "Address is required.";
      if (!intake.country) e.country = "Please select your country.";
    }
    if (s === 3) {
      if (!intake.under_care) e.under_care = "Please select Yes or No.";
      if (!intake.taking_medication) e.taking_medication = "Please select Yes or No.";
      HEALTH_FLAGS.forEach((f) => {
        const v = intake.health_flags[f.key];
        if (v !== "yes" && v !== "no") e[`flag_${f.key}`] = "Please select Yes or No.";
      });
      if (anyYesInStep3 && !intake.health_details.trim()) {
        e.health_details = "Please provide details — this is essential for your safety.";
      }
    }
    if (s === 5) {
      ACK_ITEMS.forEach((a) => {
        if (!intake.acknowledgements[a.key]) e[`ack_${a.key}`] = "Please tick to confirm.";
      });
      if (!intake.recording_consent) e.recording_consent = "Please select one option.";
      if (!intake.esign_consent) e.esign_consent = "Please tick to consent to electronic signing.";
      const norm = (v) => v.trim().replace(/\s+/g, " ").toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const expectedName = norm(`${intake.first_name} ${intake.last_name}`);
      const typed = norm(intake.signature_name);
      if (!typed) e.signature_name = "Please type your full name to sign.";
      else if (typed !== expectedName) e.signature_name = `Signature must match "${intake.first_name} ${intake.last_name}".`;
    }
    return e;
  };

  const stepErrors = useMemo(() => validate(step), [step, date, time, intake, anyYesInStep3]); // eslint-disable-line react-hooks/exhaustive-deps
  const canProceed = Object.keys(stepErrors).length === 0;
  // Only actually display errors on fields once the user has attempted to proceed
  const errors = attempted ? stepErrors : {};

  const scrollToFirstError = (errObj) => {
    const firstKey = Object.keys(errObj)[0];
    if (!firstKey) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-error-key="${firstKey}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 80);
  };

  const next = () => {
    if (!canProceed) {
      setAttempted(true);
      setErr("Please complete the highlighted fields before continuing.");
      scrollToFirstError(stepErrors);
      return;
    }
    setErr(""); setAttempted(false);
    setStep((s) => Math.min(s + 1, 5));
  };
  const back = () => { setErr(""); setAttempted(false); setStep((s) => Math.max(s - 1, 1)); };

  const handleCheckout = async () => {
    if (!canProceed) {
      setAttempted(true);
      setErr("Please complete the highlighted fields before signing.");
      scrollToFirstError(stepErrors);
      return;
    }
    setErr("");
    if (!service) return;
    setLoading(true);
    trackEvent("booking_submitted", { service_id: service.id });
    try {
      const fullPhone = `${intake.phone_dial} ${intake.phone.trim()}`;
      const r = await axios.post(`${API}/checkout/session`, {
        package_id: service.id,
        origin_url: window.location.origin,
        booking: {
          date: date.toISOString().slice(0, 10),
          time, format,
          name: `${intake.first_name} ${intake.last_name}`.trim(),
          email: intake.email,
        },
        intake: {
          ...intake,
          phone: fullPhone,
          submitted_at: new Date().toISOString(),
          timezone_offset_minutes: new Date().getTimezoneOffset(),
        },
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      const msg = parseApiError(e);
      setErr(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-4xl bg-[#0B0E14] border border-[#D4AF37]/20 text-white p-0 overflow-hidden max-h-[88vh] flex flex-col rounded-2xl"
      >
        <DialogHeader className="px-8 pt-8 pb-3">
          <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]">book your session</p>
          <DialogTitle className="font-serif text-3xl text-white mt-1">
            {service?.title || ""}
          </DialogTitle>
          <DialogDescription className="font-sans text-sm text-slate-400 mt-2">
            5 steps · client consent included · secured by Stripe
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="px-8 pb-3" data-testid="booking-stepper">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <div key={s.id} className="flex items-center flex-1">
                  <div className={`flex items-center gap-2 ${active ? "text-[#D4AF37]" : done ? "text-white" : "text-slate-500"}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${
                      active ? "border-[#D4AF37] bg-[#D4AF37]/10" :
                      done ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" :
                      "border-white/10"
                    }`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="hidden sm:inline font-sans text-[11px] uppercase tracking-overline">
                      {s.title}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-px mx-2 ${done ? "bg-[#D4AF37]/40" : "bg-white/5"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-8 pb-6 flex-1 min-h-0 overflow-y-auto">
          {/* STEP 1 — SESSION */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="step-1">
              <div>
                <Label>Choose a date <span className="text-[#D4AF37]">*</span></Label>
                <div
                  data-error-key="date"
                  className={`rounded-xl border bg-white/[0.02] p-2 transition-colors ${
                    errors.date ? "border-red-400/70 bg-red-500/[0.04]" : "border-white/10"
                  }`}
                  data-testid="booking-calendar"
                >
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={isDateBlocked}
                    className="text-white"
                    classNames={{
                      day_today:
                        "bg-transparent text-[#D4AF37] ring-1 ring-inset ring-[#D4AF37]/40",
                      day_selected:
                        "bg-[#D4AF37] text-black hover:bg-[#D4AF37] hover:text-black focus:bg-[#D4AF37] focus:text-black ring-0",
                    }}
                  />
                </div>
                {errors.date && (
                  <p className="mt-1.5 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3" /> {errors.date}
                  </p>
                )}
                <Label className="mt-6">Choose a time <span className="text-[#D4AF37]">*</span></Label>
                <div
                  data-error-key="time"
                  className={`grid grid-cols-3 gap-2 p-1 rounded-xl transition-colors ${
                    errors.time ? "ring-1 ring-red-400/50 bg-red-500/[0.02]" : ""
                  }`}
                  data-testid="booking-time-grid"
                >
                  {times.map((t) => {
                    const iso = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}` : "";
                    const slotBlocked = !!iso && (availability.blocked_times?.[iso] || []).includes(t);
                    return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`time-${t.replace(":","")}`}
                      onClick={() => !slotBlocked && setTime(t)}
                      disabled={slotBlocked}
                      title={slotBlocked ? "Unavailable" : undefined}
                      className={`py-2.5 rounded-lg text-sm font-sans border transition-all ${
                        slotBlocked
                          ? "bg-white/[0.01] border-white/5 text-slate-600 line-through cursor-not-allowed"
                          : time === t
                          ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                          : "bg-white/[0.02] border-white/10 text-slate-200 hover:border-[#D4AF37]/40"
                      }`}
                    >
                      {formatSlot(t)}
                    </button>
                    );
                  })}
                </div>
                {errors.time && (
                  <p className="mt-1.5 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3" /> {errors.time}
                  </p>
                )}
              </div>
              <div>
                <Label>Format</Label>
                <div className="rounded-xl border border-[#D4AF37] bg-[#D4AF37]/10 p-4" data-testid="format-online">
                  <Video className="w-5 h-5 text-[#D4AF37]" />
                  <p className="mt-2 font-serif text-lg text-white">Online video session</p>
                  <p className="text-xs text-slate-400 mt-1">Held securely via Doxy.me — encrypted telehealth platform.</p>
                  <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                    Online sessions provide the same depth as in-person sessions: regression therapy works
                    through the subconscious and superconscious mind, which are not limited by physical location.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — ABOUT YOU */}
          {step === 2 && (
            <div data-testid="step-2">
              <SectionTitle overline="01" title="Client details" subtitle="Fields marked with * are required." />
              <Grid2>
                <FieldText required label="First name" value={intake.first_name} onChange={(v) => setField("first_name", v)} testid="intake-first-name" error={errors.first_name} errorKey="first_name" />
                <FieldText required label="Last name" value={intake.last_name} onChange={(v) => setField("last_name", v)} testid="intake-last-name" error={errors.last_name} errorKey="last_name" />
                <FieldText required label="Date of birth" type="date" value={intake.dob} onChange={(v) => setField("dob", v)} testid="intake-dob" error={errors.dob} errorKey="dob" />
                <FieldText required label="Email" type="email" value={intake.email} onChange={(v) => setField("email", v)} testid="intake-email" error={errors.email} errorKey="email" />
                <PhoneField
                  required
                  dial={intake.phone_dial}
                  onDialChange={(v) => setField("phone_dial", v)}
                  value={intake.phone}
                  onChange={(v) => setField("phone", v)}
                  testid="intake-phone"
                  error={errors.phone}
                  errorKey="phone"
                />
                <FieldText required label="Address" value={intake.address} onChange={(v) => setField("address", v)} testid="intake-address" error={errors.address} errorKey="address" />
                <CountrySelect required value={intake.country} onChange={(v) => setField("country", v)} testid="intake-country" error={errors.country} errorKey="country" />
              </Grid2>

              <SectionTitle overline="02" title="Emergency contact" subtitle="Optional — recommended for in-person continuity of care." className="mt-8" />
              <Grid2>
                <FieldText label="Name" hint="optional" value={intake.emergency_name} onChange={(v) => setField("emergency_name", v)} testid="intake-emergency-name" />
                <FieldText label="Phone" hint="optional" type="tel" value={intake.emergency_phone} onChange={(v) => setField("emergency_phone", v)} testid="intake-emergency-phone" />
              </Grid2>

              <p className="mt-6 text-xs text-slate-400 font-sans flex items-center gap-2">
                <Lock className="w-3 h-3" /> Information is treated with care and confidentiality, in accordance with Australian privacy laws.
              </p>
            </div>
          )}

          {/* STEP 3 — HEALTH */}
          {step === 3 && (
            <div data-testid="step-3">
              <SectionTitle overline="03" title="Health & wellbeing" subtitle="All answers are required. This helps ensure your safety and suitability for hypnosis." />

              <YesNoRow
                required
                label="Are you currently under the care of a doctor, psychologist, or psychiatrist?"
                value={intake.under_care}
                onChange={(v) => setField("under_care", v)}
                testid="under-care"
                error={errors.under_care}
                errorKey="under_care"
              />
              <YesNoRow
                required
                label="Are you currently taking any medications (including for mental health)?"
                value={intake.taking_medication}
                onChange={(v) => setField("taking_medication", v)}
                testid="taking-medication"
                error={errors.taking_medication}
                errorKey="taking_medication"
              />

              <p className="mt-7 mb-3 font-sans text-sm text-slate-300">
                Have you ever experienced or been diagnosed with any of the following?{" "}
                <span className="text-[#D4AF37]">*</span>
              </p>
              <div className="space-y-2">
                {HEALTH_FLAGS.map((f) => (
                  <YesNoRow
                    key={f.key}
                    required
                    label={f.label}
                    value={intake.health_flags[f.key]}
                    onChange={(v) => setNested("health_flags", f.key, v)}
                    testid={`flag-${f.key}`}
                    error={errors[`flag_${f.key}`]}
                    errorKey={`flag_${f.key}`}
                    compact
                  />
                ))}
              </div>

              <div data-error-key="health_details">
                <Label className="mt-6">
                  If you answered yes to any of the above, please provide details
                  {anyYesInStep3 && <span className="text-[#D4AF37]"> *</span>}
                </Label>
                <Textarea
                  data-testid="health-details"
                  value={intake.health_details}
                  onChange={(e) => setField("health_details", e.target.value)}
                  placeholder={anyYesInStep3
                    ? "Required — please share what Kassie should know..."
                    : "Anything you'd like Kassie to know..."}
                  aria-invalid={!!errors.health_details}
                  className={`cosmic-input rounded-xl px-4 py-3 w-full font-sans text-sm min-h-[110px] transition-colors ${
                    errors.health_details
                      ? "!border-red-400/80 focus:!border-red-400 !bg-red-500/[0.04]"
                      : anyYesInStep3 && !intake.health_details.trim()
                      ? "border-[#D4AF37]/60"
                      : ""
                  }`}
                />
                {errors.health_details ? (
                  <p className="mt-1.5 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3" /> {errors.health_details}
                  </p>
                ) : anyYesInStep3 && !intake.health_details.trim() ? (
                  <p className="mt-2 text-xs text-[#D4AF37]">
                    Please provide details before continuing — this is essential for your safety.
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {/* STEP 4 — INTENTIONS */}
          {step === 4 && (
            <div data-testid="step-4">
              <SectionTitle overline="04" title="Personal background" subtitle="This entire step is optional — share only what feels right." />
              <FieldArea label="Current challenges or patterns you would like to explore" hint="optional" value={intake.current_challenges}
                onChange={(v) => setField("current_challenges", v)} testid="bg-challenges" />
              <FieldArea label="Fears, phobias, or recurring emotions" hint="optional" value={intake.fears_phobias}
                onChange={(v) => setField("fears_phobias", v)} testid="bg-fears" />
              <FieldArea label="Significant life events or experiences (if comfortable sharing)" hint="optional" value={intake.significant_events}
                onChange={(v) => setField("significant_events", v)} testid="bg-events" />
              <FieldArea label="Dreams, intuitive experiences, or spiritual interests" hint="optional" value={intake.spiritual_interests}
                onChange={(v) => setField("spiritual_interests", v)} testid="bg-spiritual" />

              <SectionTitle overline="05" title="Session intentions" subtitle="Optional. Up to five questions or areas you would like to explore." className="mt-8" />
              <div className="space-y-3">
                {intake.intentions.map((q, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="font-serif text-2xl text-[#D4AF37]/70 shrink-0 leading-none mt-2 w-6 text-right">{i + 1}</span>
                    <input
                      data-testid={`intention-${i + 1}`}
                      value={q}
                      onChange={(e) => {
                        const arr = [...intake.intentions];
                        arr[i] = e.target.value;
                        setField("intentions", arr);
                      }}
                      placeholder={`Question or theme ${i + 1} (optional)`}
                      className="cosmic-input rounded-xl px-4 py-3 w-full font-sans text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 5 — CONSENT */}
          {step === 5 && (
            <div data-testid="step-5">
              <SectionTitle overline="06" title="Important information" subtitle="Please read carefully before signing." />
              <ul className="space-y-2 mb-6 font-sans text-sm">
                {[
                  "Sessions involve guided hypnosis and deep relaxation.",
                  "You remain aware and in control at all times.",
                  "Experiences may be symbolic, intuitive, or interpretive.",
                  "Outcomes cannot be guaranteed and vary between individuals.",
                  "These services are complementary and not a substitute for medical, psychological, or psychiatric care.",
                ].map((t) => (
                  <li key={t} className="flex gap-3 items-start text-slate-300">
                    <span className="mt-2 w-1 h-1 rounded-full bg-[#D4AF37] shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              <SectionTitle overline="07" title="Consent to participate *" subtitle="All statements below are required. Tick each one to confirm." className="mt-2" />
              <div className="space-y-2.5">
                {ACK_ITEMS.map((a) => {
                  const hasError = !!errors[`ack_${a.key}`];
                  return (
                    <label
                      key={a.key}
                      data-testid={`ack-${a.key}`}
                      data-error-key={`ack_${a.key}`}
                      className={`flex items-start gap-3 px-4 py-3 rounded-xl bg-white/[0.02] border cursor-pointer transition-colors ${
                        hasError ? "border-red-400/70 bg-red-500/[0.04]" : "border-white/10 hover:border-[#D4AF37]/30"
                      }`}
                    >
                      <Checkbox
                        checked={intake.acknowledgements[a.key]}
                        onCheckedChange={(v) => setNested("acknowledgements", a.key, !!v)}
                        className={`mt-0.5 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-black ${
                          hasError ? "border-red-400" : "border-[#D4AF37]/50"
                        }`}
                      />
                      <span className="font-sans text-sm text-slate-200 leading-relaxed">
                        {a.label}
                        {hasError && <span className="block mt-0.5 text-xs text-red-300/90">Please tick to confirm.</span>}
                      </span>
                    </label>
                  );
                })}
              </div>

              <SectionTitle overline="08" title="Recording consent *" subtitle="Required. Please select one option. Recordings are stored securely and used only for the purpose you select." className="mt-7" />
              <div data-error-key="recording_consent">
                <RadioGroup
                  value={intake.recording_consent}
                  onValueChange={(v) => setField("recording_consent", v)}
                  className="space-y-2.5"
                  data-testid="recording-consent"
                >
                  {[
                    { v: "yes", label: "I consent to my session being recorded for anonymised educational or case study purposes." },
                    { v: "no",  label: "I do not consent to recording." },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      className={`flex items-start gap-3 px-4 py-3 rounded-xl bg-white/[0.02] border cursor-pointer transition-colors ${
                        errors.recording_consent ? "border-red-400/70 bg-red-500/[0.04]" : "border-white/10 hover:border-[#D4AF37]/30"
                      }`}
                    >
                      <RadioGroupItem
                        value={opt.v}
                        data-testid={`recording-${opt.v}`}
                        className={`mt-0.5 text-[#D4AF37] ${errors.recording_consent ? "border-red-400" : "border-[#D4AF37]/50"}`}
                      />
                      <span className="font-sans text-sm text-slate-200 leading-relaxed">{opt.label}</span>
                    </label>
                  ))}
                </RadioGroup>
                {errors.recording_consent && (
                  <p className="mt-1.5 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3" /> {errors.recording_consent}
                  </p>
                )}
              </div>

              {/* E-Signature */}
              <SectionTitle overline="09" title="Electronic signature *" subtitle="Legally valid in Australia under the Electronic Transactions Act 1999 (Cth)." className="mt-7" />
              <div
                className={`rounded-2xl border bg-[#D4AF37]/[0.04] p-5 space-y-4 transition-colors ${
                  errors.esign_consent || errors.signature_name ? "border-red-400/70 bg-red-500/[0.04]" : "border-[#D4AF37]/30"
                }`}
              >
                <label className="flex items-start gap-3 cursor-pointer" data-testid="esign-consent" data-error-key="esign_consent">
                  <Checkbox
                    checked={intake.esign_consent}
                    onCheckedChange={(v) => setField("esign_consent", !!v)}
                    className={`mt-0.5 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-black ${
                      errors.esign_consent ? "border-red-400" : "border-[#D4AF37]"
                    }`}
                  />
                  <span className="font-sans text-sm text-slate-200 leading-relaxed">
                    I consent to electronic signing and acknowledge that typing my full name below has the same legal effect as a handwritten signature.
                    I have read, understood, and agree to the Client Consent & Intake form, and consent to proceed with my session under these terms.
                    {errors.esign_consent && (
                      <span className="block mt-1 text-xs text-red-300/90 flex items-center gap-1.5">
                        <AlertCircle className="w-3 h-3" /> {errors.esign_consent}
                      </span>
                    )}
                  </span>
                </label>

                <div data-error-key="signature_name">
                  <Label>Type your full name to sign *</Label>
                  <input
                    data-testid="signature-input"
                    type="text"
                    value={intake.signature_name}
                    onChange={(e) => setField("signature_name", e.target.value)}
                    placeholder={`${intake.first_name} ${intake.last_name}`.trim() || "Your full legal name (must match step 2)"}
                    aria-invalid={!!errors.signature_name}
                    className={`cosmic-input rounded-xl px-4 py-4 w-full font-serif italic text-2xl transition-colors ${
                      errors.signature_name ? "!border-red-400/80 focus:!border-red-400 !bg-red-500/[0.04]" : ""
                    }`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  />
                  {errors.signature_name && (
                    <p className="mt-2 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
                      <AlertCircle className="w-3 h-3" /> {errors.signature_name}
                    </p>
                  )}
                </div>

                <p className="text-[11px] text-slate-400 font-sans leading-relaxed">
                  <ShieldCheck className="w-3 h-3 inline mr-1 text-[#D4AF37]" />
                  Audit trail captured: signed name · date / time · IP address · browser. Document is locked after signing.
                </p>
              </div>

              {/* Summary */}
              <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-5 grid grid-cols-2 gap-3 text-sm font-sans">
                <Summary k="Session" v={service?.title || ""} />
                <Summary k="Format" v={format === "in_person" ? "In person" : "Online video"} />
                <Summary k="Date" v={date ? date.toLocaleDateString() : "—"} />
                <Summary k="Time" v={time ? formatSlot(time) : "—"} />
                <Summary k="Total" v={service ? `$${service.price} AUD` : "—"} highlight />
              </div>
            </div>
          )}

          {err && <p className="mt-5 text-sm text-red-300/90 font-sans" data-testid="booking-error">{err}</p>}
        </div>

        {/* Footer / nav */}
        <div className="px-8 py-5 border-t border-white/5 flex items-center justify-between bg-[#040508]/60 shrink-0">
          <button
            type="button"
            data-testid="booking-back-button"
            onClick={back}
            disabled={step === 1}
            className="btn-ghost-gold rounded-full px-5 py-2.5 text-xs flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>

          <span className="font-sans text-xs tracking-overline text-slate-400">
            Step {step} <span className="text-slate-600">·</span> {STEPS[step - 1].title}
          </span>

          {step < 5 ? (
            <button
              type="button"
              data-testid="booking-next-button"
              onClick={next}
              aria-disabled={!canProceed}
              className={`btn-gold rounded-full px-6 py-2.5 text-xs flex items-center gap-2 ${!canProceed ? "opacity-50" : ""}`}
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="booking-checkout-button"
              onClick={handleCheckout}
              aria-disabled={!canProceed || loading}
              disabled={loading}
              className={`btn-gold rounded-full px-6 py-2.5 text-xs flex items-center gap-2 ${(!canProceed || loading) ? "opacity-50" : ""}`}
            >
              {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting...</> : <>Sign &amp; pay ${service?.price ?? ""} AUD <Lock className="w-3.5 h-3.5" /></>}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---- Small helpers ---- */
const Label = ({ children, className = "" }) => (
  <p className={`font-sans text-[10px] uppercase tracking-overline text-slate-400 mb-2 ${className}`}>{children}</p>
);
const SectionTitle = ({ overline, title, subtitle, className = "" }) => (
  <div className={className}>
    <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mb-1">{overline}</p>
    <h3 className="font-serif text-2xl text-white">{title}</h3>
    {subtitle && <p className="mt-1 font-sans text-sm text-slate-400">{subtitle}</p>}
    <div className="h-px bg-white/5 my-5" />
  </div>
);
const Grid2 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;

function FieldText({ label, type = "text", value, onChange, testid, wide, required, hint, error, errorKey }) {
  const hasError = !!error;
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`} data-error-key={errorKey}>
      <Label>
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
        {hint && !required && <span className="text-slate-500 normal-case tracking-normal ml-2">({hint})</span>}
      </Label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={hasError}
        className={`cosmic-input rounded-xl px-4 py-3 w-full font-sans text-base transition-colors ${
          hasError ? "!border-red-400/80 focus:!border-red-400 !bg-red-500/[0.04]" : ""
        }`}
      />
      {hasError && (
        <p className="mt-1.5 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}
    </label>
  );
}
function FieldArea({ label, value, onChange, testid, hint, required, error, errorKey }) {
  const hasError = !!error;
  return (
    <label className="block mb-4" data-error-key={errorKey}>
      <Label>
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
        {hint && !required && <span className="text-slate-500 normal-case tracking-normal ml-2">({hint})</span>}
      </Label>
      <Textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={hasError}
        className={`cosmic-input rounded-xl px-4 py-3 w-full font-sans text-sm min-h-[80px] transition-colors ${
          hasError ? "!border-red-400/80 focus:!border-red-400 !bg-red-500/[0.04]" : ""
        }`}
      />
      {hasError && (
        <p className="mt-1.5 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}
    </label>
  );
}
function YesNoRow({ label, value, onChange, testid, required, compact, error, errorKey }) {
  const hasError = !!error;
  return (
    <div
      data-error-key={errorKey}
      className={`${compact ? "mb-2" : "mb-4"} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 rounded-xl bg-white/[0.02] border transition-colors ${
        hasError ? "border-red-400/70 bg-red-500/[0.04]" : "border-white/10"
      }`}
    >
      <p className="font-sans text-sm text-slate-200 flex-1">
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
        {hasError && (
          <span className="block mt-1 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" /> {error}
          </span>
        )}
      </p>
      <div className="flex gap-2 shrink-0">
        {["yes", "no"].map((v) => (
          <button
            key={v}
            type="button"
            data-testid={`${testid}-${v}`}
            onClick={() => onChange(v)}
            className={`px-5 py-1.5 rounded-full text-xs font-sans uppercase tracking-overline border transition-all ${
              value === v
                ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#D4AF37]/40"
            }`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}
function FormatBtn({ icon: Icon, label, hint, active, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`p-4 rounded-xl border text-left transition-all ${
        active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40"
      }`}
    >
      <Icon className="w-5 h-5 text-[#D4AF37]" />
      <p className="mt-2 font-serif text-lg text-white">{label}</p>
      <p className="text-xs text-slate-400 mt-1">{hint}</p>
    </button>
  );
}
function Summary({ k, v, highlight }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-overline text-[#D4AF37]/80">{k}</p>
      <p className={`mt-1 ${highlight ? "font-serif text-2xl text-white" : "text-slate-200"}`}>{v}</p>
    </div>
  );
}
